import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMediaQuery } from 'usehooks-ts';
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { minWidth } from '../css/breakpoints';
import useElementSize from './hooks/useElementSize';
import type { Size } from './hooks/useElementSize';
import './ZoomableImage.scss';

interface Point {
  left: number;
  top: number;
}

interface Rect extends Point, Size {}

interface ZoomableImageProps {
  src: string;
  className?: string;
  // The pane is portalled out of here: only the parent can place and size it.
  paneContainer: HTMLElement | null;
  paneSize: Size;
  children?: ReactNode;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function hasArea(size: Size): boolean {
  return size.width > 0 && size.height > 0;
}

function useNaturalSize(src: string): Size | null {
  const [size, setSize] = useState<Size | null>(null);

  useEffect(() => {
    setSize(null);
    const image = new Image();
    image.onload = () =>
      setSize({ width: image.naturalWidth, height: image.naturalHeight });
    image.src = src;

    return () => {
      image.onload = null;
    };
  }, [src]);

  return size;
}

interface Zoom {
  natural: Size;
  // Natural pixels per painted pixel.
  scale: number;
  // Where the image lands inside its box: `background-size: contain` fills one
  // axis only, so pointer maths has to run against this rather than the box.
  painted: Rect;
  // The slice of the painted image the pane shows, in painted pixels.
  lens: Size;
  // The pane draws the image at natural size, so a source smaller than the pane
  // has to be centred in the leftover space.
  paneOffset: Point;
}

function buildZoom(natural: Size, box: Size, pane: Size): Zoom {
  const fit = Math.min(box.width / natural.width, box.height / natural.height);
  const painted = {
    width: natural.width * fit,
    height: natural.height * fit,
    left: (box.width - natural.width * fit) / 2,
    top: (box.height - natural.height * fit) / 2,
  };

  return {
    natural,
    scale: 1 / fit,
    painted,
    lens: {
      width: Math.min(pane.width * fit, painted.width),
      height: Math.min(pane.height * fit, painted.height),
    },
    paneOffset: {
      left: Math.max(pane.width - natural.width, 0) / 2,
      top: Math.max(pane.height - natural.height, 0) / 2,
    },
  };
}

// Pointer position to lens top-left, clamped inside the painted image.
function lensOrigin(pointer: number, lens: number, bound: number): number {
  return clamp(pointer - lens / 2, 0, bound - lens);
}

export default function ZoomableImage({
  src,
  className,
  paneContainer,
  paneSize,
  children,
}: ZoomableImageProps) {
  const [imageEl, setImageEl] = useState<HTMLDivElement | null>(null);
  const [active, setActive] = useState(false);
  const lensEl = useRef<HTMLSpanElement>(null);
  const paneEl = useRef<HTMLDivElement>(null);
  // Lens top-left, relative to the painted image rather than the box.
  const lensPosition = useRef<Point>({ left: 0, top: 0 });

  const naturalSize = useNaturalSize(src);
  const boxSize = useElementSize(imageEl);
  const isDesktop = useMediaQuery(minWidth('sm'));

  // No pane to draw into means no zoom at all, rather than a lens over nothing.
  const zoom =
    isDesktop &&
    paneContainer &&
    naturalSize &&
    boxSize &&
    hasArea(boxSize) &&
    hasArea(paneSize)
      ? buildZoom(naturalSize, boxSize, paneSize)
      : null;

  const positionZoom = () => {
    if (!zoom) return;

    const { left, top } = lensPosition.current;

    if (lensEl.current) {
      lensEl.current.style.left = `${zoom.painted.left + left}px`;
      lensEl.current.style.top = `${zoom.painted.top + top}px`;
    }
    if (paneEl.current) {
      const x = zoom.paneOffset.left - left * zoom.scale;
      const y = zoom.paneOffset.top - top * zoom.scale;
      paneEl.current.style.backgroundPosition = `${x}px ${y}px`;
    }
  };

  // Places them on the render that mounts them, before the browser paints.
  useLayoutEffect(positionZoom);

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse') return;
    if (!zoom || !imageEl) return;

    const box = imageEl.getBoundingClientRect();
    const { painted, lens } = zoom;
    lensPosition.current = {
      left: lensOrigin(
        event.clientX - box.left - painted.left,
        lens.width,
        painted.width,
      ),
      top: lensOrigin(
        event.clientY - box.top - painted.top,
        lens.height,
        painted.height,
      ),
    };

    positionZoom();
    if (!active) setActive(true);
  };

  return (
    <>
      <div
        ref={setImageEl}
        className={['image-zoom', className].filter(Boolean).join(' ')}
        style={{ backgroundImage: `url(${src})` }}
        onPointerMove={handlePointerMove}
        onPointerLeave={() => setActive(false)}
      >
        {children}
        {zoom && active && (
          <span
            ref={lensEl}
            className="image-zoom__lens"
            style={{ width: zoom.lens.width, height: zoom.lens.height }}
          />
        )}
      </div>

      {zoom &&
        active &&
        paneContainer &&
        createPortal(
          <div
            ref={paneEl}
            className="image-zoom__pane"
            style={{
              width: paneSize.width,
              height: paneSize.height,
              backgroundImage: `url(${src})`,
              backgroundSize: `${zoom.natural.width}px ${zoom.natural.height}px`,
            }}
          />,
          paneContainer,
        )}
    </>
  );
}
